import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronDown,
  Menu,
  X,
  Check,
  TrendingUp,
  Settings,
  Sparkles,
  BarChart3,
  Users,
  DollarSign,
  Building2,
  BookOpen
} from "lucide-react";
import logo from "./media/newlogo12.png";
import { triggerIntroSplash } from "../IntroSplash/IntroSplash";
import TopNav from "./TopNav";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogoClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    triggerIntroSplash();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "auto" });
    } else {
      navigate("/");
    }
  };

  // When the user clicks the nav link for the page they're already on,
  // React Router won't change the route (so the app's scroll-to-top on
  // route change never fires). Scroll to the top manually in that case.
  const handleNavClick = (path) => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
    if (location.pathname.toLowerCase() === path.toLowerCase()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const isActivePath = (path) =>
    location.pathname.toLowerCase() === path.toLowerCase();

  // Any /services/:serviceId page — used to mark the Services nav item so it
  // stays obvious which section the visitor is in after the menu closes.
  // The desktop dropdown is a fixed 900px panel that covers the page behind it,
  // so every link inside it goes through handleNavClick — without that it stayed
  // open after navigating and hid the service page the visitor just picked.
  const isServiceRoute = location.pathname.toLowerCase().startsWith("/services");

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const services = [
    {
      category: "Operational Excellence",
      icon: Settings,
      color: "#264653",
      items: [
        { 
          name: "Process & Productivity Improvement", 
          path: "/services/process-optimization",
          icon: TrendingUp,
          desc: "Streamline workflows" 
        },
        { 
          name: "Menu Engineering & Innovation", 
          path: "/services/menu-engineering",
          icon: Sparkles,
          desc: "Design profitable menus"
        },
        { 
          name: "Kitchen Performance Optimization", 
          path: "/services/kitchen-operations",
          icon: Settings,
          desc: "Optimize kitchen efficiency"
        },
        { 
          name: "Inventory & Cost Control", 
          path: "/services/inventory-management",
          icon: BarChart3,
          desc: "Control stock & costs"
        },
      ],
    },
    {
      category: "GROWTH & VISIBILITY",
      icon: TrendingUp,
      color: "#2a9d8f",
      items: [
        { 
          name: "Branding & Marketing", 
          path: "/services/branding-marketing",
          icon: Sparkles,
          desc: "Build your brand"
        },
        { 
          name: "Digital Presence Management", 
          path: "/services/digital-profiling",
          icon: TrendingUp,
          desc: "Online presence"
        },
        { 
          name: "Delivery Revenue Management", 
          path: "/services/delivery-platforms",
          icon: Building2,
          desc: "Platform management"
        },
        { 
          name: "Customer Relationship", 
          path: "/services/crm",
          icon: Users,
          desc: "CRM solutions"
        },
        { 
          name: "Catering & Events", 
          path: "/services/catering-events",
          icon: Sparkles,
          desc: "Event management"
        },
      ],
    },
    {
      category: "MANAGEMENT & SUSTAINABILITY",
      icon: Building2,
      color: "#e76f51",
      items: [
        { 
          name: "Financial Control & Reporting", 
          path: "/services/financial-accounts",
          icon: DollarSign,
          desc: "Financial management"
        },
        { 
          name: "Team Performance & Development", 
          path: "/services/employee-management",
          icon: Users,
          desc: "Team development"
        },
        { 
          name: "Infrastructure & Asset Management", 
          path: "/services/infrastructure-management",
          icon: Building2,
          desc: "Facility maintenance"
        },
      ],
    },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-white shadow-md" 
            : "bg-white/95 backdrop-blur-sm"
        }`}
      >
        <TopNav />
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="relative z-10" onClick={handleLogoClick}>
              <motion.img
                src={logo}
                alt="Terces Solutions"
                className="h-11"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1 relative">
              {/* Home */}
              <Link
                to="/"
                onClick={() => handleNavClick("/")}
                className="px-4 py-2 text-[15px] font-semibold text-gray-700 hover:text-primary rounded-lg hover:bg-primary/5 transition-all duration-200"
              >
                Home
              </Link>

              {/* Services Dropdown */}
              <div
                className=""
                onMouseEnter={() => setActiveDropdown('services')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  className={`relative flex items-center gap-1.5 px-4 py-2 text-[15px] font-semibold rounded-lg hover:bg-primary/5 transition-all duration-200 ${
                    isServiceRoute
                      ? "text-primary bg-primary/5"
                      : "text-gray-700 hover:text-primary"
                  }`}
                >
                  Services
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${
                      activeDropdown === 'services' ? 'rotate-180' : ''
                    }`}
                  />
                  {isServiceRoute && (
                    <span className="absolute bottom-0.5 left-4 right-7 h-0.5 rounded-full bg-primary" />
                  )}
                </button>

   <AnimatePresence>
  {activeDropdown === 'services' && (
<motion.div
  initial={{ opacity: 0, y: 10 }}
  animate={{ opacity: 1, y: 0 }}
  exit={{ opacity: 0, y: 10 }}
  transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
style={{
  // Main nav row (80px) + contact top bar (36px)
  position: "fixed",
  top: "116px",
  left: "30vw",
  transform: "translateX(-50%)",
  zIndex: 50,
}}

>
  <div
    style={{
      position: "absolute",
      top: "-24px",
      left: 0,
      right: 0,
      height: "24px",
    }}
  />

      <div className="bg-white rounded-2xl shadow-2xl border-2 border-gray-100 p-8 w-[900px] mx-auto">
        {/* Decorative top accent */}
        <div className="absolute top-2  h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50 rounded-t-2xl" />
        
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-1">Our Services</h3>
          <p className="text-sm text-gray-500">Comprehensive solutions for your restaurant business</p>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {services.map((cat, i) => {
            const CategoryIcon = cat.icon;
            return (
              <div key={i} className="space-y-3">
                {/* Category Header */}
                <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-gray-100">
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${cat.color}15` }}
                  >
                    <CategoryIcon 
                      className="w-4 h-4" 
                      style={{ color: cat.color }}
                      strokeWidth={2}
                    />
                  </div>
                  <h4 className="text-[12px] font-bold uppercase tracking-wider" style={{ color: cat.color }}>
                    {cat.category}
                  </h4>
                </div>

                {/* Service Items */}
                <ul className="space-y-1">
                  {cat.items.map((item, j) => {
                    const ItemIcon = item.icon;
                    const isActive = isActivePath(item.path);
                    return (
                      <li key={j}>
                        <Link
                          to={item.path}
                          onClick={() => handleNavClick(item.path)}
                          aria-current={isActive ? "page" : undefined}
                          className={`group flex items-start gap-3 p-3 rounded-xl transition-all duration-200 ${
                            isActive
                              ? "bg-primary/10 ring-1 ring-primary/30"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <div
                            className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 ${
                              isActive
                                ? "bg-primary"
                                : "bg-primary/5 group-hover:bg-primary group-hover:scale-110"
                            }`}
                          >
                            <ItemIcon
                              className={`w-4 h-4 transition-colors ${
                                isActive
                                  ? "text-white"
                                  : "text-primary group-hover:text-white"
                              }`}
                              strokeWidth={2}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`text-[13px] font-semibold transition-colors leading-tight mb-0.5 ${
                                isActive
                                  ? "text-primary"
                                  : "text-gray-900 group-hover:text-primary"
                              }`}
                            >
                              {item.name}
                            </div>
                            <div
                              className={`text-[11px] leading-tight ${
                                isActive
                                  ? "text-primary/80 font-semibold"
                                  : "text-gray-500"
                              }`}
                            >
                              {isActive ? "You're viewing this" : item.desc}
                            </div>
                          </div>
                          {isActive && (
                            <Check
                              className="w-4 h-4 text-primary flex-shrink-0 mt-0.5"
                              strokeWidth={3}
                            />
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div className="mt-6 pt-6 border-t border-gray-100 flex items-center justify-between">
          <p className="text-sm text-gray-600">Need help choosing the right service?</p>
          <Link
            to="/Contact"
            onClick={() => handleNavClick("/Contact")}
            className="px-5 py-2 bg-primary hover:bg-primary-dark text-white text-sm font-semibold rounded-lg transition-all hover:scale-105"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </motion.div>
  )}
</AnimatePresence>
              </div>

              {/* About Dropdown */}
       
{/* About Dropdown */}
<div
  className="relative"
  onMouseEnter={() => setActiveDropdown('about')}
  onMouseLeave={() => setActiveDropdown(null)}
>
  <button className="flex items-center gap-1.5 px-4 py-2 text-[15px] font-semibold text-gray-700 hover:text-primary rounded-lg hover:bg-primary/5 transition-all duration-200">
    About
    <ChevronDown 
      size={16} 
      className={`transition-transform duration-200 ${
        activeDropdown === 'about' ? 'rotate-180' : ''
      }`} 
    />
  </button>

  <AnimatePresence>
    {activeDropdown === 'about' && (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-0 top-full pt-1 z-50"
      >
        {/* Invisible bridge to prevent gap */}
        <div className="h-1 w-full" />
        
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-gray-100 p-3 overflow-hidden w-64">
          {/* Decorative top accent */}
          <div className="absolute top-1 left-0 right-0 h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50 rounded-t-2xl" />
          
          <div className="space-y-1">
            <Link
              to="/Ourstory"
              onClick={() => handleNavClick("/Ourstory")}
              className="group flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-all duration-200"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-200">
                <BookOpen className="w-5 h-5 text-primary group-hover:text-white transition-colors" strokeWidth={2} />
              </div>
              <div className="flex-1 pt-1">
                <div className="text-[15px] font-semibold text-gray-900 group-hover:text-primary transition-colors leading-tight">
                  Our Story
                </div>
                <div className="text-[11px] text-gray-500 leading-tight mt-0.5">
                  Learn about our journey
                </div>
              </div>
            </Link>
            
            <Link
              to="/Team"
              onClick={() => handleNavClick("/Team")}
              className="group flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-all duration-200"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-200">
                <Users className="w-5 h-5 text-primary group-hover:text-white transition-colors" strokeWidth={2} />
              </div>
              <div className="flex-1 pt-1">
                <div className="text-[15px] font-semibold text-gray-900 group-hover:text-primary transition-colors leading-tight">
                  Our Team
                </div>
                <div className="text-[11px] text-gray-500 leading-tight mt-0.5">
                  Meet our experts
                </div>
              </div>
            </Link>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
</div>
              {/* Case Studies */}
              <Link
                to="/CaseStudies"
                onClick={() => handleNavClick("/CaseStudies")}
                className="px-4 py-2 text-[15px] font-semibold text-gray-700 hover:text-primary rounded-lg hover:bg-primary/5 transition-all duration-200"
              >
                Case Studies
              </Link>

              {/* Resources */}
              <Link
                to="/Resources"
                onClick={() => handleNavClick("/Resources")}
                className="px-4 py-2 text-[15px] font-semibold text-gray-700 hover:text-primary rounded-lg hover:bg-primary/5 transition-all duration-200"
              >
                Resources
              </Link>

              {/* Blogs */}
              <Link
                to="/Blogs"
                onClick={() => handleNavClick("/Blogs")}
                className="px-4 py-2 text-[15px] font-semibold text-gray-700 hover:text-primary rounded-lg hover:bg-primary/5 transition-all duration-200"
              >
                Blogs
              </Link>

              {/* Careers */}
              <Link
                to="/Careers"
                onClick={() => handleNavClick("/Careers")}
                className="px-4 py-2 text-[15px] font-semibold text-gray-700 hover:text-primary rounded-lg hover:bg-primary/5 transition-all duration-200"
              >
                Careers
              </Link>

              {/* Contact CTA */}
              <Link
                to="/Contact"
                onClick={() => handleNavClick("/Contact")}
                className="ml-2 px-6 py-2.5 rounded-full text-white font-semibold text-[15px] bg-primary hover:bg-primary-dark shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
              >
                Contact Us
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu (keeping previous mobile code) */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="lg:hidden bg-white border-t-2 border-gray-100 overflow-hidden"
            >
              <div className="max-w-[1400px] mx-auto px-6 py-6 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 116px)', WebkitOverflowScrolling: 'touch' }}>
                <Link
                  to="/"
                  className="block px-4 py-3 text-[15px] font-semibold text-gray-700 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                  onClick={() => handleNavClick("/")}
                >
                  Home
                </Link>

                <div>
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === 'services-mobile' ? null : 'services-mobile')}
                    className="flex items-center justify-between w-full px-4 py-3 text-[15px] font-semibold text-gray-700 hover:bg-primary/5 rounded-lg transition-all"
                  >
                    <span className={isServiceRoute ? "text-primary" : ""}>
                      Services
                    </span>
                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-200 ${
                        activeDropdown === 'services-mobile' ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <AnimatePresence>
                    {activeDropdown === 'services-mobile' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="pl-4 mt-2 space-y-4 overflow-hidden"
                      >
                        {services.map((cat, i) => (
                          <div key={i} className="space-y-2">
                            <h4 className="text-[12px] font-bold text-primary uppercase tracking-wider px-4 py-2">
                              {cat.category}
                            </h4>
                            <ul className="space-y-1">
                              {cat.items.map((item, j) => {
                                const ItemIcon = item.icon;
                                const isActive = isActivePath(item.path);
                                return (
                                <li key={j}>
                                  <Link
                                    to={item.path}
                                    aria-current={isActive ? "page" : undefined}
                                    className={`flex items-center gap-3 px-4 py-2 text-[14px] rounded-lg transition-all ${
                                      isActive
                                        ? "bg-primary/10 text-primary font-semibold ring-1 ring-primary/30"
                                        : "text-gray-600 hover:text-primary hover:bg-primary/5"
                                    }`}
                                    onClick={() => handleNavClick(item.path)}
                                  >
                                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                      isActive ? "bg-primary" : "bg-primary/5"
                                    }`}>
                                      <ItemIcon
                                        className={`w-3.5 h-3.5 ${isActive ? "text-white" : "text-primary"}`}
                                        strokeWidth={2}
                                      />
                                    </div>
                                    <span className="flex-1">{item.name}</span>
                                    {isActive && (
                                      <Check className="w-4 h-4 text-primary flex-shrink-0" strokeWidth={3} />
                                    )}
                                  </Link>
                                </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div>
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === 'about-mobile' ? null : 'about-mobile')}
                    className="flex items-center justify-between w-full px-4 py-3 text-[15px] font-semibold text-gray-700 hover:bg-primary/5 rounded-lg transition-all"
                  >
                    About
                    <ChevronDown 
                      size={18} 
                      className={`transition-transform duration-200 ${
                        activeDropdown === 'about-mobile' ? 'rotate-180' : ''
                      }`} 
                    />
                  </button>
                  <AnimatePresence>
                    {activeDropdown === 'about-mobile' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="pl-4 mt-2 space-y-1 overflow-hidden"
                      >
                        <Link
                          to="/Ourstory"
                          className="flex items-center gap-3 px-4 py-2 text-[14px] text-gray-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                          onClick={() => handleNavClick("/Ourstory")}
                        >
                          <div className="w-7 h-7 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
                            <BookOpen className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                          </div>
                          Our Story
                        </Link>
                        <Link
                          to="/Team"
                          className="flex items-center gap-3 px-4 py-2 text-[14px] text-gray-600 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                          onClick={() => handleNavClick("/Team")}
                        >
                          <div className="w-7 h-7 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0">
                            <Users className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                          </div>
                          Our Team
                        </Link>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Link
                  to="/CaseStudies"
                  className="block px-4 py-3 text-[15px] font-semibold text-gray-700 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                  onClick={() => handleNavClick("/CaseStudies")}
                >
                  Case Studies
                </Link>

                <Link
                  to="/Resources"
                  className="block px-4 py-3 text-[15px] font-semibold text-gray-700 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                  onClick={() => handleNavClick("/Resources")}
                >
                  Resources
                </Link>

                <Link
                  to="/Blogs"
                  className="block px-4 py-3 text-[15px] font-semibold text-gray-700 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                  onClick={() => handleNavClick("/Blogs")}
                >
                  Blogs
                </Link>

                <Link
                  to="/Careers"
                  className="block px-4 py-3 text-[15px] font-semibold text-gray-700 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                  onClick={() => handleNavClick("/Careers")}
                >
                  Careers
                </Link>

                <Link
                  to="/Contact"
                  className="block mt-4 px-6 py-3.5 text-center text-[15px] rounded-full text-white font-semibold bg-primary shadow-lg hover:shadow-xl transition-all"
                  onClick={() => handleNavClick("/Contact")}
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};

export default Header;